import { useEffect, useState, useCallback, type ReactNode, type FormEvent } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { RecordPicker } from '../../components/RecordPicker.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable, Modal } from '../../../../shared/components/index.js';
import {
  ApprovalLevels,
  type ApprovalTrailStep,
} from '../../components/ApprovalLevels.js';
import { useAutoRefresh } from '../../../../shared/hooks/useAutoRefresh.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';

interface BudgetPlan {
  readonly planId: string;
  readonly planCode: string;
  readonly planName: string;
  readonly financialYear: string;
  readonly status: string;
  readonly allocatedAmountInr: number;
  readonly consumedAmountInr: number;
  readonly reservedAmountInr: number;
  readonly availableAmountInr: number;
  readonly utilizationPct: number;
  readonly effectiveFromDate: string;
  readonly effectiveToDate: string;
  readonly approvedAt: string | null;
  readonly approvedByName: string | null;
  readonly workflowInstanceId?: string | null;
  /** Server-decided: the plan waits on (or names) an approval request that
   *  does not exist, so no approver can act on it and its trail cannot be read. */
  readonly workflowInstanceMissing?: boolean;
  /** Server-decided: SUBMITTED, request missing, and the caller holds the
   *  right the withdraw-stale endpoint is guarded by. */
  readonly canWithdrawStale?: boolean;
  readonly version: number;
}

interface PlanVersion {
  readonly versionId: string;
  readonly versionNo: number;
  readonly planName: string;
  readonly allocatedAmountInr: number;
  readonly effectiveFromDate: string;
  readonly effectiveToDate: string;
  readonly status: string;
  readonly changeReason: string | null;
  /** Every entry of the budget as approved in this version; null when the
   *  version was saved before entries were kept with it. */
  readonly lines: ReadonlyArray<{
    readonly periodCode: string | null;
    readonly amountInr: number;
    readonly members: ReadonlyArray<{ readonly dimensionCode: string; readonly memberName: string }>;
  }> | null;
  readonly approvedAt: string | null;
  readonly createdByName: string | null;
  readonly createdAt: string;
}

interface Allocation {
  readonly allocationId: string;
  readonly allocationCode: string;
  readonly dimensionCode: string;
  readonly dimensionValue: string;
  readonly allocatedAmountInr: number;
  readonly consumedAmountInr: number;
}

interface Consumption {
  readonly consumptionId: string;
  readonly jobNumber: string | null;
  readonly consumptionTypeCode: string;
  readonly amountInr: number;
  readonly createdAt: string;
}

/** Registry row from `t606_budget_dimension`; what a tenant may budget by. */
interface Dimension {
  readonly dimensionCode: string;
  readonly dimensionName: string;
}

interface BoundWorkflowStep {
  readonly stepSequence: number;
  readonly stepName: string;
  readonly approverRoleCode: string | null;
}

interface BoundWorkflow {
  readonly bound: boolean;
  readonly reason?: string;
  readonly workflowCode?: string;
  readonly workflowName?: string;
  readonly steps?: BoundWorkflowStep[];
}

const CARD: React.CSSProperties = {
  padding: 16, borderRadius: 8, marginBottom: 16,
  border: '1px solid var(--color-border-default)',
  background: 'var(--color-bg-elevated)',
};
const LABEL: React.CSSProperties = { fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 2 };
const VAL: React.CSSProperties = { fontSize: 14, fontWeight: 500, marginBottom: 12 };


export function ShBudgetDetailPage(): ReactNode {
  const { planId } = useParams<{ planId: string }>();
  const { token } = useAuth();
  const { t } = useTranslation();
  // Amounts follow the tenant's DISPLAY_NUMBER_FORMAT, not a locale in the source.
  const { money, number } = useBusinessNumber();
  // Instants follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { day, at } = useBusinessDay();

  const [plan, setPlan] = useState<BudgetPlan | null>(null);
  const [versions, setVersions] = useState<PlanVersion[]>([]);
  const [allocations, setAllocations] = useState<Allocation[]>([]);
  const [consumption, setConsumption] = useState<Consumption[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [flow, setFlow] = useState<BoundWorkflow | null>(null);
  const [trail, setTrail] = useState<readonly ApprovalTrailStep[]>([]);
  const [trailError, setTrailError] = useState<string | null>(null);

  // Withdraw stale submission
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [withdrawReason, setWithdrawReason] = useState('');
  const [withdrawError, setWithdrawError] = useState<string | null>(null);

  // Allocation form
  const [showAllocForm, setShowAllocForm] = useState(false);
  const [dimensions, setDimensions] = useState<Dimension[]>([]);
  const [dimCode, setDimCode] = useState('');
  const [dimVal, setDimVal] = useState('');
  const [allocAmt, setAllocAmt] = useState('');

  // Consumption form
  const [showConsForm, setShowConsForm] = useState(false);
  const [consJob, setConsJob] = useState('');
  const [consAmt, setConsAmt] = useState('');
  const [consType, setConsType] = useState('CONSUME');

  const load = useCallback(async () => {
    if (!planId) return;
    try {
      // Every list here is shown as a figure or a picker; an unread one must
      // not stand in as "no allocations" or "no versions".
      const [p, a, c, d, v] = await Promise.all([
        apiRequest<BudgetPlan>(`/v1/budget/plans/${encodeURIComponent(planId)}`, { token }),
        apiRequest<Allocation[]>(`/v1/budget/plans/${encodeURIComponent(planId)}/allocations`, { token }),
        apiRequest<Consumption[]>(`/v1/budget/plans/${encodeURIComponent(planId)}/consumption`, { token }),
        apiRequest<Dimension[]>('/v1/budget/dimensions', { token }),
        apiRequest<PlanVersion[]>(`/v1/budget/plans/${encodeURIComponent(planId)}/versions`, { token }),
      ]);
      setPlan(p); setAllocations(a); setConsumption(c); setDimensions(d); setVersions(v);
      // Keep whatever the user already picked; otherwise start on the first
      // dimension the tenant actually configured.
      setDimCode((prev) => prev || d[0]?.dimensionCode || '');
      setError(null);
    } catch (e) { setError((e as Error).message); }
  }, [planId, token]);

  useEffect(() => { void load(); }, [load]);

  // Where the submitted plan has reached. The person who raised it holds no
  // workflow permission, so this reads the stakeholder trail rather than the
  // approver's instance view.
  const loadTrail = useCallback(async () => {
    const instanceId = plan?.workflowInstanceId;
    // The server has already said there is no request to read; the notice
    // below states it, so asking anyway would only fetch a 404.
    if (!instanceId || plan?.workflowInstanceMissing) {
      setTrail([]); setTrailError(null); return;
    }
    try {
      const r = await apiRequest<{ data: { stepTrail: ApprovalTrailStep[] } }>(
        `/v1/wf/instances/${instanceId}/trail`, { token },
      );
      setTrail(r.data?.stepTrail ?? []);
      setTrailError(null);
    } catch (e) {
      // An unread trail is not an empty one: say so instead of showing nothing.
      setTrail([]);
      setTrailError((e as Error).message);
    }
  }, [plan?.workflowInstanceId, plan?.workflowInstanceMissing, token]);

  useEffect(() => { void loadTrail(); }, [loadTrail]);

  // The owner watches this page waiting for an answer, so it fetches its own.
  // A plan whose request is missing will never get one, so it is not polled.
  useAutoRefresh(
    async () => { await load(); await loadTrail(); },
    { active: plan?.status === 'SUBMITTED' && !plan.workflowInstanceMissing },
  );

  // Which workflow this plan will enter. Read separately so a workflow module
  // that is unreachable never blocks the plan itself from loading.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const r = await apiRequest<BoundWorkflow>(
          '/v1/wf/active-binding?processCode=BUDGET_APPROVAL', { token },
        );
        if (!cancelled) setFlow(r);
      } catch { if (!cancelled) setFlow(null); }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const handleAlloc = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true); setError(null); setInfo(null);
    try {
      await apiRequest(`/v1/budget/plans/${encodeURIComponent(planId!)}/allocations`, {
        method: 'POST', token,
        body: { planId, dimensionCode: dimCode, dimensionValue: dimVal, allocatedAmountInr: Number(allocAmt) },
      });
      setInfo(t('sh.budgetDetail.allocCreated'));
      setShowAllocForm(false); setDimVal(''); setAllocAmt('');
      await load();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }, [planId, dimCode, dimVal, allocAmt, token, busy, load]);

  const handleConsumption = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true); setError(null); setInfo(null);
    try {
      await apiRequest('/v1/budget/consumption', {
        method: 'POST', token,
        body: { planId, jobRequestId: consJob, amountInr: Number(consAmt), consumptionTypeCode: consType },
      });
      setInfo(t('sh.budgetDetail.consRecorded', { type: consType }));
      setShowConsForm(false); setConsJob(''); setConsAmt('');
      await load();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }, [planId, consJob, consAmt, consType, token, busy, load]);

  const handleSubmitForApproval = useCallback(async () => {
    if (busy) return;
    setBusy(true); setError(null); setInfo(null);
    try {
      await apiRequest(`/v1/budget/plans/${encodeURIComponent(planId!)}/submit`, {
        method: 'POST', token, body: {},
      });
      setInfo(t('sh.budgetDetail.submitted'));
      await load();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }, [planId, token, busy, load]);

  const closeWithdraw = useCallback(() => {
    setWithdrawOpen(false); setWithdrawReason(''); setWithdrawError(null);
  }, []);

  // Returns a submission whose approval request no longer exists to DRAFT, so
  // the ordinary Submit above becomes available again.
  const handleWithdrawStale = useCallback(async () => {
    const reason = withdrawReason.trim();
    if (busy || !reason) return;
    setBusy(true); setWithdrawError(null); setError(null); setInfo(null);
    try {
      await apiRequest(`/v1/budget/plans/${encodeURIComponent(planId!)}/withdraw-stale`, {
        method: 'POST', token, body: { reason },
      });
      closeWithdraw();
      setInfo(t('sh.budgetDetail.withdrawStaleDone'));
      await load();
    } catch (e) { setWithdrawError((e as Error).message); }
    finally { setBusy(false); }
  }, [planId, token, busy, withdrawReason, closeWithdraw, load]);

  if (!plan && !error) return <main aria-label="sh-budget-detail"><p>{t('sh.common.loading')}</p></main>;

  return (
    <main aria-label="sh-budget-detail">
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16 }}>
        <Link to="/sh/budget" data-testid="back-budget-list">{t('sh.budgetDetail.backList')}</Link>
        <h1 style={{ fontSize: 20 }}>{plan?.planName ?? t('sh.budgetDetail.fallbackTitle')}</h1>
      </div>

      {error && <p role="alert" style={{ color: 'var(--color-danger)', marginBottom: 12 }}>{error}</p>}
      {info && <p role="status" style={{ color: 'var(--color-success)', marginBottom: 12 }}>{info}</p>}

      {plan && (
        <>
          <section style={CARD} aria-label="plan-summary">
            <h2 style={{ fontSize: 16, marginBottom: 12 }}>{t('sh.budgetDetail.planSummary')}</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
              <div><div style={LABEL}>{t('sh.budgetList.colCode')}</div><div style={VAL}><code>{plan.planCode}</code></div></div>
              <div><div style={LABEL}>{t('sh.common.financialYear')}</div><div style={VAL}>{plan.financialYear}</div></div>
              <div><div style={LABEL}>{t('sh.common.status')}</div><div style={VAL}>{plan.status}</div></div>
              <div><div style={LABEL}>{t('sh.budgetDetail.allocated')}</div><div style={VAL}>₹{money(plan.allocatedAmountInr)}</div></div>
              <div><div style={LABEL}>{t('sh.budgetDetail.consumed')}</div><div style={VAL}>₹{money(plan.consumedAmountInr)}</div></div>
              <div><div style={LABEL}>{t('sh.budgetDetail.reserved')}</div><div style={VAL}>₹{money(plan.reservedAmountInr)}</div></div>
              <div><div style={LABEL}>{t('sh.budgetDetail.available')}</div><div style={VAL}>₹{money(plan.availableAmountInr)}</div></div>
              <div><div style={LABEL}>{t('sh.budgetDetail.utilization')}</div><div style={{ ...VAL, color: plan.utilizationPct > 80 ? 'var(--color-danger)' : 'inherit' }}>{number(plan.utilizationPct)}%</div></div>
              <div><div style={LABEL}>{t('sh.common.period')}</div><div style={VAL}>{day(plan.effectiveFromDate)} – {day(plan.effectiveToDate)}</div></div>
              <div><div style={LABEL}>{t('sh.budgetList.colApprovedOn')}</div><div style={VAL} data-testid="plan-approved-on">{at(plan.approvedAt)}</div></div>
              <div><div style={LABEL}>{t('sh.budgetList.colApprovedBy')}</div><div style={VAL} data-testid="plan-approved-by">{plan.approvedByName ?? (plan.approvedAt ? '—' : t('sh.budgetList.notApproved'))}</div></div>
              <div><div style={LABEL}>{t('sh.budgetList.colVersion')}</div><div style={VAL} data-testid="plan-version">{plan.version > 0 ? t('sh.budgetList.versionLabel', { n: plan.version }) : '—'}</div></div>
            </div>

            {/* Newest first: the top row is the budget actually in force. */}
            <h3 style={{ fontSize: 14, margin: '16px 0 8px' }}>{t('sh.budgetList.versionHistory')}</h3>
            <DataTable<PlanVersion>
              ariaLabel="budget-versions-table"
              rows={versions}
              rowKey={(v) => v.versionId}
              columns={[
                { key: 'no', header: t('sh.budgetList.colVersion'), render: (v) => (
                  <span data-testid={`ver-${v.versionNo}`}>
                    {t('sh.budgetList.versionLabel', { n: v.versionNo })}
                    {v.versionNo === plan.version && <strong style={{ marginLeft: 6, color: 'var(--color-success)' }}>· {t('sh.budgetList.inForce')}</strong>}
                  </span>
                ) },
                { key: 'name', header: t('sh.budgetList.colName'), render: (v) => v.planName },
                { key: 'amt', header: t('sh.common.allocatedInr'), render: (v) => <>₹{money(v.allocatedAmountInr)}</> },
                { key: 'period', header: t('sh.common.period'), render: (v) => <span style={{ fontSize: 12 }}>{day(v.effectiveFromDate)} – {day(v.effectiveToDate)}</span> },
                { key: 'status', header: t('sh.common.status'), render: (v) => v.status },
                { key: 'entries', header: t('sh.budgetList.colEntries'), render: (v) => (
                  v.lines === null
                    ? <span style={{ fontSize: 12 }}>{t('sh.budgetList.entriesNotRecorded')}</span>
                    : v.lines.length === 0
                      ? <span style={{ fontSize: 12 }}>{t('sh.budgetList.entriesNone')}</span>
                      : (
                        <ul data-testid={`ver-entries-${v.versionNo}`} style={{ margin: 0, paddingLeft: 16, fontSize: 12 }}>
                          {v.lines.map((e, i) => (
                            <li key={i}>
                              {e.members.map((m) => m.memberName).join(' · ')}
                              {e.periodCode ? ` (${e.periodCode})` : ''} — ₹{money(e.amountInr)}
                            </li>
                          ))}
                        </ul>
                      )
                ) },
                { key: 'reason', header: t('sh.budgetList.changeReason'), render: (v) => v.changeReason ?? '—' },
                { key: 'by', header: t('sh.budgetList.colSavedBy'), render: (v) => <span style={{ fontSize: 12 }}>{v.createdByName ?? '—'}</span> },
                { key: 'at', header: t('sh.budgetList.colSavedOn'), render: (v) => <span style={{ fontSize: 12 }}>{at(v.createdAt)}</span> },
                { key: 'appr', header: t('sh.budgetList.colApprovedOn'), render: (v) => <span style={{ fontSize: 12 }}>{v.approvedAt ? at(v.approvedAt) : t('sh.budgetList.notApproved')}</span> },
              ]}
            />

            {/* Approval state is spelled out because "DRAFT" alone does not tell
                anyone that the money cannot be spent yet. */}
            {plan.status === 'DRAFT' && (
              <p role="status" data-testid="budget-draft-notice" style={{ marginTop: 12 }}>
                {plan.version > 0
                  ? t('sh.budgetDetail.revisionDraftNotice', { n: plan.version })
                  : t('sh.budgetDetail.draftNotice')}
              </p>
            )}
            {plan.status === 'SUBMITTED' && !plan.workflowInstanceMissing && (
              <p role="status" data-testid="budget-awaiting-approval" style={{ marginTop: 12 }}>
                {plan.version > 0
                  ? t('sh.budgetDetail.revisionAwaitingApproval', { n: plan.version })
                  : t('sh.budgetDetail.awaitingApproval')}
              </p>
            )}
            {/* Said by the server, not guessed from a failed trail read. */}
            {plan.workflowInstanceMissing && (
              <p role="status" data-testid="budget-instance-missing" style={{ marginTop: 12, color: 'var(--color-danger)' }}>
                {t(plan.status === 'SUBMITTED'
                  ? 'sh.budgetDetail.instanceMissing'
                  : 'sh.budgetDetail.instanceMissingDecided')}
              </p>
            )}
            {trailError && (
              <p role="alert" data-testid="budget-trail-error" style={{ marginTop: 12, color: 'var(--color-danger)' }}>
                {t('sh.budgetDetail.trailUnavailable', { message: trailError })}
              </p>
            )}
            {plan.status === 'SUBMITTED' && plan.workflowInstanceMissing && plan.canWithdrawStale && (
              <Button
                type="button"
                variant="danger"
                onClick={() => { setWithdrawError(null); setWithdrawOpen(true); }}
                disabled={busy}
                data-testid="btn-withdraw-stale"
                style={{ marginTop: 12 }}
              >
                {t('sh.budgetDetail.withdrawStale')}
              </Button>
            )}
            <Modal
              open={withdrawOpen}
              onClose={closeWithdraw}
              title={t('sh.budgetDetail.withdrawStale')}
              closeLabel={t('sh.common.cancel')}
              data-testid="withdraw-stale-dialog"
              footer={(
                <>
                  <Button type="button" variant="secondary" onClick={closeWithdraw} data-testid="withdraw-stale-cancel">
                    {t('sh.common.cancel')}
                  </Button>
                  <Button
                    type="button"
                    variant="danger"
                    onClick={() => void handleWithdrawStale()}
                    disabled={busy || !withdrawReason.trim()}
                    data-testid="withdraw-stale-confirm"
                  >
                    {t('sh.budgetDetail.withdrawStaleConfirm')}
                  </Button>
                </>
              )}
            >
              <p style={{ marginTop: 0 }}>{t('sh.budgetDetail.withdrawStaleIntro', { code: plan.planCode })}</p>
              <label htmlFor="withdraw-stale-reason" style={{ fontSize: 12 }}>
                {t('sh.budgetDetail.withdrawStaleReason')}
              </label>
              <textarea
                id="withdraw-stale-reason"
                data-testid="withdraw-stale-reason"
                rows={3}
                required
                value={withdrawReason}
                onChange={(e) => setWithdrawReason(e.target.value)}
                style={{ width: '100%', marginTop: 4 }}
              />
              {withdrawError && (
                <p role="alert" data-testid="withdraw-stale-error" style={{ color: 'var(--color-danger)', marginBottom: 0 }}>
                  {withdrawError}
                </p>
              )}
            </Modal>
            {trail.length > 0 && (
              <div data-testid="budget-approval-levels">
                <ApprovalLevels steps={trail} />
              </div>
            )}
            {plan.status === 'REJECTED' && (
              <p role="status" data-testid="budget-rejected" style={{ marginTop: 12 }}>
                {t('sh.budgetDetail.rejected')}
              </p>
            )}
            {(plan.status === 'DRAFT' || plan.status === 'REJECTED') && (
              <>
                {flow && (
                  <div
                    data-testid="budget-bound-workflow"
                    style={{
                      marginTop: 12, padding: 12, borderRadius: 6,
                      border: '1px solid var(--color-border-default)',
                      background: 'var(--color-bg-muted)',
                    }}
                  >
                    {flow.bound ? (
                      <>
                        <div style={LABEL}>{t('sh.budgetDetail.willGoTo')}</div>
                        <div style={{ fontSize: 14, fontWeight: 500 }}>{flow.workflowName}</div>
                        <ol style={{ margin: '8px 0 0 18px', padding: 0, fontSize: 13 }}>
                          {(flow.steps ?? []).map((s) => (
                            <li key={s.stepSequence}>
                              {s.stepName}
                              {s.approverRoleCode ? ` — ${s.approverRoleCode}` : ''}
                            </li>
                          ))}
                        </ol>
                      </>
                    ) : (
                      <p role="alert" style={{ margin: 0, color: 'var(--color-danger)', fontSize: 13 }}>
                        {t('sh.budgetDetail.noWorkflowBound')}
                      </p>
                    )}
                  </div>
                )}
                <Button
                  type="button"
                  variant="primary"
                  onClick={() => void handleSubmitForApproval()}
                  disabled={busy || (flow != null && !flow.bound)}
                  data-testid="btn-submit-for-approval"
                  style={{ marginTop: 12 }}
                >
                  {t('sh.budgetDetail.submitForApproval')}
                </Button>
              </>
            )}
          </section>

          {/* ── Allocations ──────────────────────────── */}
          <section style={CARD} aria-label="allocations">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h2 style={{ fontSize: 16 }}>{t('sh.budgetDetail.allocations')}</h2>
              <Button type="button" variant="secondary" onClick={() => setShowAllocForm(!showAllocForm)} data-testid="btn-new-alloc">{t('sh.budgetDetail.add')}</Button>
            </div>
            {showAllocForm && (
              <form onSubmit={handleAlloc} aria-label="alloc-form" style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <div>
                    <label htmlFor="al-dim" style={{ fontSize: 11 }}>{t('sh.budgetDetail.dimension')}</label>
                    <select id="al-dim" value={dimCode} onChange={(e) => setDimCode(e.target.value)} data-testid="select-dim"
                            disabled={dimensions.length === 0}
                            style={{ padding: 6, borderRadius: 4, border: '1px solid var(--color-border-default)' }}>
                      {dimensions.map((d) => (
                        <option key={d.dimensionCode} value={d.dimensionCode}>{d.dimensionName}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="al-val" style={{ fontSize: 11 }}>{t('sh.budgetDetail.value')}</label>
                    <input id="al-val" value={dimVal} onChange={(e) => setDimVal(e.target.value)} required data-testid="input-dim-val"
                           style={{ padding: 6, borderRadius: 4, border: '1px solid var(--color-border-default)' }} />
                  </div>
                  <div>
                    <label htmlFor="al-amt" style={{ fontSize: 11 }}>{t('sh.budgetDetail.amount')}</label>
                    <input id="al-amt" type="number" value={allocAmt} onChange={(e) => setAllocAmt(e.target.value)} required data-testid="input-alloc-amt"
                           style={{ padding: 6, borderRadius: 4, border: '1px solid var(--color-border-default)' }} />
                  </div>
                  <Button type="submit" variant="primary" disabled={busy} data-testid="btn-submit-alloc">{t('sh.common.save')}</Button>
                </div>
              </form>
            )}
            {allocations.length === 0 ? <p style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>{t('sh.budgetDetail.noAllocations')}</p> : (
              <DataTable<Allocation>
                testId="allocations-table"
                rows={allocations}
                rowKey={(a) => a.allocationId}
                columns={[
                  { key: 'code', header: t('sh.budgetList.colCode'), render: (a) => <code style={{ fontSize: 12 }}>{a.allocationCode}</code> },
                  { key: 'dimension', header: t('sh.budgetDetail.dimension'), render: (a) => a.dimensionCode },
                  { key: 'value', header: t('sh.budgetDetail.value'), render: (a) => a.dimensionValue },
                  { key: 'allocated', header: t('sh.common.allocatedInr'), render: (a) => <>₹{money(a.allocatedAmountInr)}</> },
                  { key: 'consumed', header: t('sh.common.consumedInr'), render: (a) => <>₹{money(a.consumedAmountInr)}</> },
                ]}
              />
            )}
          </section>

          {/* ── Consumption ──────────────────────────── */}
          <section style={CARD} aria-label="consumption">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h2 style={{ fontSize: 16 }}>{t('sh.budgetDetail.consumptionHistory')}</h2>
              <Button type="button" variant="secondary" onClick={() => setShowConsForm(!showConsForm)} data-testid="btn-new-cons">{t('sh.budgetDetail.recordBtn')}</Button>
            </div>
            {showConsForm && (
              <form onSubmit={handleConsumption} aria-label="consumption-form" style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <div>
                    <label htmlFor="co-type" style={{ fontSize: 11 }}>{t('sh.common.type')}</label>
                    <select id="co-type" value={consType} onChange={(e) => setConsType(e.target.value)} data-testid="select-cons-type"
                            style={{ padding: 6, borderRadius: 4, border: '1px solid var(--color-border-default)' }}>
                      <option value="CONSUME">{t('sh.budgetDetail.typeConsume')}</option><option value="RESERVE">{t('sh.budgetDetail.typeReserve')}</option>
                      <option value="RELEASE">{t('sh.budgetDetail.typeRelease')}</option><option value="ADJUSTMENT">{t('sh.budgetDetail.typeAdjustment')}</option>
                    </select>
                  </div>
                  <div>
                    <RecordPicker<{ jobRequestId: string; jobNumber?: string; customerName?: string }>
                      endpoint="/v1/job-requests?limit=200"
                      idField="jobRequestId"
                      label={t('sh.budgetDetail.jobRequestId')}
                      testId="input-cons-job"
                      value={consJob}
                      optionLabel={(j) =>
                        [j.jobNumber, j.customerName].filter(Boolean).join(' \u2014 ') ||
                        j.jobRequestId
                      }
                      onPick={setConsJob}
                      emptyText="No job request on file"
                    />
                  </div>
                  <div>
                    <label htmlFor="co-amt" style={{ fontSize: 11 }}>{t('sh.budgetDetail.amount')}</label>
                    <input id="co-amt" type="number" value={consAmt} onChange={(e) => setConsAmt(e.target.value)} required data-testid="input-cons-amt"
                           style={{ padding: 6, borderRadius: 4, border: '1px solid var(--color-border-default)' }} />
                  </div>
                  <Button type="submit" variant="primary" disabled={busy} data-testid="btn-submit-cons">{t('sh.budgetDetail.record')}</Button>
                </div>
              </form>
            )}
            {consumption.length === 0 ? <p style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>{t('sh.budgetDetail.noConsumption')}</p> : (
              <DataTable<Consumption>
                testId="consumption-table"
                rows={consumption}
                rowKey={(c) => c.consumptionId}
                columns={[
                  { key: 'type', header: t('sh.common.type'), render: (c) => c.consumptionTypeCode },
                  { key: 'job', header: t('sh.common.job'), render: (c) => c.jobNumber ?? '—' },
                  { key: 'amount', header: t('sh.common.amountInr'), render: (c) => <>₹{money(c.amountInr)}</> },
                  { key: 'date', header: t('sh.common.date'), render: (c) => at(c.createdAt) },
                ]}
              />
            )}
          </section>
        </>
      )}
    </main>
  );
}
